import type { ReactNode } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { ADMIN_ROLES, adminCan, type AdminRole, type AdminSurface } from "@talenthop/shared";
import { AppShell, type NavGroup, type NavItem } from "../../components/AppShell.js";
import {
  Grid,
  Coins,
  Card,
  Users,
  Rows,
  Bolt,
  Alert,
  Calendar,
  Clock,
  ShieldCheck,
  FileText,
  Link2,
} from "../../components/icons.js";
import { useAuth } from "../../lib/AuthContext.js";
import { initialsOf } from "../../lib/initials.js";
import { Overview } from "./Overview.js";

/**
 * A nav item declares the SURFACE the screen behind it reads. The same capability model gates the
 * API (`requireSurface`), so hiding an item here and refusing the request there stay in step — an
 * Admin never sees a link whose every request would 403. Items with no `surface` (My security) are
 * self-service and reachable by every admin role.
 */
interface AdminNavItem extends NavItem {
  surface?: AdminSurface;
}
interface AdminNavGroup {
  label: string;
  items: AdminNavItem[];
}

const GROUPS: AdminNavGroup[] = [
  {
    label: "Command",
    items: [{ to: "/admin", label: "Overview", Icon: Grid, end: true, surface: "money" }],
  },
  {
    label: "Money",
    items: [
      { to: "/admin/plans", label: "Plans & coupons", Icon: Coins, surface: "money" },
      { to: "/admin/credit-pricing", label: "Credit pricing (legacy)", Icon: Coins, surface: "money" },
      { to: "/admin/spend", label: "Spend & model cost", Icon: Coins, surface: "money" },
      { to: "/admin/billing", label: "Revenue", Icon: Card, surface: "money" },
      { to: "/admin/payments", label: "Payments", Icon: Card, surface: "money" },
    ],
  },
  {
    label: "Tenants",
    items: [
      { to: "/admin/orgs", label: "Client orgs", Icon: Users, surface: "client_orgs" },
      { to: "/admin/signup-requests", label: "Signup requests", Icon: Users, surface: "client_orgs" },
      { to: "/admin/partners", label: "Partners", Icon: Coins, surface: "money" },
      { to: "/admin/sourcing", label: "Sourcing", Icon: Rows, surface: "sourcing" },
    ],
  },
  {
    label: "Platform",
    items: [
      { to: "/admin/regions", label: "Regions & boards", Icon: Rows, surface: "platform" },
      { to: "/admin/verticals", label: "Verticals", Icon: Rows, surface: "platform" },
      { to: "/admin/taxonomy", label: "Industries & roles", Icon: Rows, surface: "platform" },
      { to: "/admin/models", label: "Calibration layer", Icon: Bolt, surface: "platform" },
      { to: "/admin/speech-quality", label: "Speech quality", Icon: Bolt, surface: "platform" },
      { to: "/admin/interview-languages", label: "Interview languages", Icon: Bolt, surface: "platform" },
      { to: "/admin/ai-models", label: "AI models & routing", Icon: Bolt, surface: "platform" },
      { to: "/admin/prompts", label: "AI prompts", Icon: Bolt, surface: "platform" },
      { to: "/admin/judge-calls", label: "Judge calls (raw)", Icon: Bolt, surface: "platform" },
      { to: "/admin/email-templates", label: "Email templates", Icon: FileText, surface: "platform" },
      { to: "/admin/providers", label: "Identity verification", Icon: ShieldCheck, surface: "platform" },
      { to: "/admin/connectors", label: "Connectors", Icon: Link2, surface: "platform" },
      { to: "/admin/invite-modes", label: "Invite modes", Icon: Bolt, surface: "platform" },
      { to: "/admin/dead-letters", label: "Dead-letter queue", Icon: Alert, surface: "platform" },
      { to: "/admin/api-errors", label: "API errors", Icon: Alert, surface: "platform" },
      { to: "/admin/feedback", label: "Product feedback", Icon: FileText, surface: "platform" },
      { to: "/admin/releases", label: "Releases", Icon: Calendar, surface: "platform" },
    ],
  },
  {
    label: "Security",
    items: [
      { to: "/admin/team", label: "Access & MFA", Icon: Users, surface: "security" },
      { to: "/admin/security", label: "My security", Icon: ShieldCheck },
      { to: "/admin/audit", label: "Audit log", Icon: Clock, surface: "security" },
      { to: "/admin/compliance", label: "Compliance", Icon: ShieldCheck, surface: "security" },
    ],
  },
];

const asAdminRole = (role: string | null | undefined): AdminRole | null =>
  role && (ADMIN_ROLES as readonly string[]).includes(role) ? (role as AdminRole) : null;

/** Keep only the items this role can actually reach; drop any group left with no visible items.
 *  An item with no surface is self-service (My security) and stays visible to every admin role. */
function visibleGroups(role: AdminRole | null): NavGroup[] {
  return GROUPS.map((g) => ({
    label: g.label,
    items: g.items.filter((i) => !i.surface || (role !== null && adminCan(role, i.surface, false))),
  })).filter((g) => g.items.length > 0);
}

/**
 * The console's landing screen. Overview reads /admin/stats, which is `money` — Owner-only under
 * the three-role model — so Admin and Analyst are sent to the first screen they can actually use
 * rather than to a 403 on every sign-in.
 */
export function AdminHome(): ReactNode {
  const { me } = useAuth();
  const role = asAdminRole(me?.role);
  if (role && adminCan(role, "money", false)) return <Overview />;
  if (role && adminCan(role, "client_orgs", false)) return <Navigate to="/admin/orgs" replace />;
  if (role && adminCan(role, "sourcing", false)) return <Navigate to="/admin/sourcing" replace />;
  return <Navigate to="/admin/security" replace />;
}

/** ADMIN persona = TalentHop's own people (owner + platform team). Theme 1 forest + lime chrome. */
export function AdminApp(): ReactNode {
  const { logout, me } = useAuth();
  const roleBadge = me?.role ? me.role.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : "Admin";
  const groups = visibleGroups(asAdminRole(me?.role));
  return (
    <AppShell
      brand="TalentHop"
      brandTag="admin"
      accent="var(--lime)"
      groups={groups}
      orgLabel="TalentHop Platform"
      orgInitials="TH"
      userInitials={initialsOf(me?.name, me?.email)}
      roleBadge={roleBadge}
      onLogout={logout}
    >
      <Outlet />
    </AppShell>
  );
}
